import { debounce } from 'lodash';
import React from 'react';
import { SortingRule } from '@components/table';
import { userSearchDebounce } from '../../components/hooks/use-search';
import { useWrapRequest } from 'use-wrap-request';
import { useSearchReducer } from '../../components/hooks/useSearchReducer';
import { Api } from '../../core/api';
import { injectTSDI } from '../../core/tsdi';

export function useWorkoutTypeMappingStore() {
    const { fitnessActivityClient } = injectTSDI(Api);

    const [sorting, setSorting] = React.useState<
        SortingRule | SortingRule[] | undefined
    >(undefined);

    const [workoutTypeFilter, setWorkoutTypeFilter] =
        React.useState<string>('');
    const [rejectedFilter, setRejectedFilter] = React.useState<
        boolean | undefined
    >(undefined);

    React.useEffect(() => {
        if (sorting) {
            dispatch({ type: 'sort', sort: sorting });
        }
    }, [sorting]);

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0
    });
    const { setSearchTerm } = userSearchDebounce(dispatch);

    const workoutTypeMappings = useWrapRequest(
        async (
            searchParams?: QueryParamsDto,
            workoutTypeFilter?: string,
            rejectedFilter?: boolean
        ) => {
            const query = {
                page: searchParams?.page || 0,
                size: searchParams?.size || 30,
                search: searchParams?.search,
                sort: searchParams?.sort,
                workoutType: workoutTypeFilter || undefined,
                rejected: rejectedFilter
            };

            const result =
                await fitnessActivityClient.admin.workoutTypeMappingAdminControllerGetPage(
                    query,
                    {}
                );

            return {
                ...result.data,
                content: (result.data.content || []).map((item, index) => ({
                    ...item,
                    id: query.page * query.size + index
                }))
            };
        },
        {
            defaultData: {
                content: [],
                totalElements: 0,
                number: 0,
                size: 30,
                totalPages: 0
            }
        }
    );

    const debouncedAPICall = React.useCallback(
        debounce(
            (
                searchParams?: QueryParamsDto,
                workoutTypeFilter?: string,
                rejectedFilter?: boolean
            ) => {
                workoutTypeMappings.request([
                    searchParams,
                    workoutTypeFilter,
                    rejectedFilter
                ]);
            },
            100
        ),
        [workoutTypeMappings]
    );

    React.useEffect(() => {
        debouncedAPICall(searchParams, workoutTypeFilter, rejectedFilter);
    }, [searchParams, workoutTypeFilter, rejectedFilter, debouncedAPICall]);

    React.useEffect(() => {
        dispatch({ type: 'page', page: 0 });
    }, [workoutTypeFilter, rejectedFilter]);

    return {
        workoutTypeMappings,
        searchParams,
        sorting,
        setSorting,
        setSearchTerm,
        dispatch,
        workoutTypeFilter,
        setWorkoutTypeFilter,
        rejectedFilter,
        setRejectedFilter
    };
}
